// Visit counting and purchase attribution for the best-esim-for-* destination
// pages. pts counts the first visit per browser per page, and the page the
// visitor last landed on is sent with the order, so the admin "Landing page
// stats" report can link sales back to it.
//
// Spec: ai_docs/specs/destination-landing-pages.md §7.1
// Backend: pts ai_docs/SPECS/SPEC_landing_page_conversion_tracking.md

import CookieType, { CookieOptions } from "@constants/CookieType";
import axios from "@utilities/axios";
import Cookies from "js-cookie";

// Keep in sync with LandingPageStats::PAGES in pts: a slug pts doesn't know is
// neither counted nor attributed.
export const LANDING_PAGES = [
  "best-esim-for-albania",
  "best-esim-for-china",
  "best-esim-for-egypt",
  "best-esim-for-europe",
  "best-esim-for-germany",
  "best-esim-for-global",
  "best-esim-for-greece",
  "best-esim-for-indonesia",
  "best-esim-for-japan",
  "best-esim-for-morocco",
  "best-esim-for-pakistan",
  "best-esim-for-thailand",
  "best-esim-for-turkiye",
  "best-esim-for-united-kingdom",
  "best-esim-for-usa",
] as const;

export type LandingPage = (typeof LANDING_PAGES)[number];

const ATTRIBUTION_DAYS = 30;
const VISIT_FLAG_PREFIX = "lp_visit_";
// Last path segment only, so locale (/de/…) and affiliate (/christian/…)
// prefixes still match.
const SLUG_PATTERN = /(best-esim-for-[a-z-]+)\/?$/;

// Pages with a /landing_page_visit/ call still in flight, so a re-render (or
// React StrictMode's double effect) can't count the same visit twice.
const pendingVisits = new Set<LandingPage>();

const isLandingPage = (value: string): value is LandingPage =>
  (LANDING_PAGES as readonly string[]).includes(value);

export const detectLandingPage = (pathname: string): LandingPage | null => {
  const match = SLUG_PATTERN.exec(pathname.toLowerCase());
  if (match === null) {
    return null;
  }
  return isLandingPage(match[1]) ? match[1] : null;
};

/** Last page wins: a later destination page overwrites the cookie. */
export const rememberLandingPage = (landingPage: LandingPage): void => {
  Cookies.set(CookieType.LANDING_PAGE, landingPage, {
    ...CookieOptions,
    expires: ATTRIBUTION_DAYS,
  });
};

/** The destination page to credit with a purchase, if any. */
export const getLandingPage = (): LandingPage | null => {
  const value = Cookies.get(CookieType.LANDING_PAGE);
  return value !== undefined && isLandingPage(value) ? value : null;
};

/**
 * Count this browser's first visit to the page. The flag is only set once pts
 * answers 2xx, so a blocked or failed call is retried on the next visit.
 */
export const trackLandingPageVisit = (landingPage: LandingPage): void => {
  if (typeof window === "undefined" || pendingVisits.has(landingPage)) {
    return;
  }
  const flagKey = `${VISIT_FLAG_PREFIX}${landingPage}`;
  try {
    if (window.localStorage.getItem(flagKey) !== null) {
      return;
    }
  } catch {
    // Storage blocked: without the flag every page view would be counted.
    return;
  }

  pendingVisits.add(landingPage);
  const formData = new FormData();
  formData.append("landing_page", landingPage);
  axios
    .post("/landing_page_visit/", formData)
    .then(() => {
      try {
        window.localStorage.setItem(flagKey, new Date().toISOString());
      } catch {
        // Quota / blocked storage: the visit is counted again next time.
      }
    })
    .catch(() => {
      // Fire-and-forget: tracking must never surface to the visitor.
    })
    .finally(() => {
      pendingVisits.delete(landingPage);
    });
};
